import React, { useCallback } from "react";
import {
    buildDocumentRefLabel,
    DocumentReference,
} from "@common/schemas/documentReference.schema";
import { DocumentIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import { useDocumentRefContextMaybe } from "@components/tasks/DocumentRefContext";
import { RefChip, RefChipProps } from "./RefChip";
import { useRefChipHoverFocus } from "./useRefChipHoverFocus";

interface DocumentRefChipProps extends Omit<RefChipProps, "icon" | "label"> {
    reference: DocumentReference;
}

export const DocumentRefChip: React.FC<DocumentRefChipProps> = React.memo(
    function DocumentRefChip({ reference, ...handlers }) {
        return (
            <RefChip
                icon={DocumentIcon}
                label={reference.label ?? buildDocumentRefLabel(reference)}
                {...handlers}
            />
        );
    },
);

/**
 * Chip rendered inside a sent message bubble. Clicking opens the same
 * financial document preview/details overlay used from the Documents page.
 */
export const MessageDocumentRefChip: React.FC<{
    reference: DocumentReference;
}> = React.memo(function MessageDocumentRefChip({ reference }) {
    const { openOverlay } = useOverlays();
    const docRefCtx = useDocumentRefContextMaybe();
    const { handleMouseEnter, handleMouseLeave } = useRefChipHoverFocus({
        id: reference.documentId,
        setFocusedId: docRefCtx?.setFocusedDocumentId,
    });

    const handleClick = useCallback(() => {
        openOverlay("financialDocumentDetailsModal", reference.documentId);
    }, [openOverlay, reference.documentId]);

    return (
        <DocumentRefChip
            reference={reference}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        />
    );
});
